import {
  Box,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useState } from "react";
import JournalEntryDetail from "@/components/accounting/JournalEntryDetail";
import { usePeriods } from "@/hooks/useAccounting";
import { PERIOD_STATUS } from "@/interfaces/accounting";
import HowThisWorks from "./HowThisWorks";
import OrgDeferralsBlotter from "./OrgDeferralsBlotter";
import OrgScheduleDetail from "./OrgScheduleDetail";
import RecognitionRunBar from "./RecognitionRunBar";
import RecognitionRunsView from "./RecognitionRunsView";
import ReconciliationDifferences from "./ReconciliationDifferences";
import ReconciliationStrip from "./ReconciliationStrip";
import RollForwardTable from "./RollForwardTable";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

type Breakdown = "month" | "organization";

type View =
  | { kind: "landing" }
  | { kind: "org"; organizationId: number }
  | { kind: "differences" }
  | { kind: "runs" }
  | { kind: "entry"; journalEntryId: number; from: View };

/** "2026-09-01" → "2026-09-30". */
function monthEnd(periodStart: string) {
  const [y, m] = periodStart.slice(0, 7).split("-").map(Number);
  return new Date(Date.UTC(y as number, m as number, 0)).toISOString().slice(0, 10);
}

/**
 * The check defaults to the last CLOSED month end: the figure finance compares with QuickBooks, and
 * one that no longer moves. With no month closed yet it falls back to today.
 */
function useDefaultAsOf(): { asOf: string | undefined; note: string } {
  const { data: periods = [] } = usePeriods();
  const lastClosed = periods
    .filter((p) => p.status === PERIOD_STATUS.closed)
    .map((p) => p.period)
    .sort()
    .at(-1);
  return lastClosed
    ? { asOf: monthEnd(lastClosed), note: "Last closed month end" }
    : { asOf: undefined, note: "Today — no month closed yet" };
}

/**
 * Deferred Subscriptions — subscription fees paid for service not yet delivered.
 *
 * The check against the books sits on top. Below it, one breakdown at a time: the year by month, or
 * by organization. An organization opens its billing periods and month lines; a journal entry opens
 * the entry itself. Each drill-down returns with Back, like Periods does.
 */
export default function DeferredSubscriptionsPanel() {
  const [fy, setFy] = useState(CURRENT_YEAR);
  const [breakdown, setBreakdown] = useState<Breakdown>("month");
  const [view, setView] = useState<View>({ kind: "landing" });
  const [pickedAsOf, setPickedAsOf] = useState<string | null>(null);
  const fallback = useDefaultAsOf();
  const asOf = pickedAsOf ?? fallback.asOf;
  const landing = () => setView({ kind: "landing" });

  if (view.kind === "entry") {
    return (
      <JournalEntryDetail
        journalEntryId={view.journalEntryId}
        onBack={() => setView(view.from)}
        backLabel={view.from.kind === "differences" ? "Reconciliation differences" : "Organization"}
      />
    );
  }
  if (view.kind === "org") {
    return (
      <OrgScheduleDetail
        organizationId={view.organizationId}
        fy={fy}
        onBack={landing}
        onOpenEntry={(journalEntryId) => setView({ kind: "entry", journalEntryId, from: view })}
      />
    );
  }
  if (view.kind === "runs") {
    return <RecognitionRunsView onBack={landing} />;
  }
  if (view.kind === "differences") {
    return (
      <ReconciliationDifferences
        asOf={asOf}
        onBack={landing}
        onOpenEntry={(journalEntryId) => setView({ kind: "entry", journalEntryId, from: view })}
      />
    );
  }

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
              Deferred subscription revenue
            </Typography>
            <HowThisWorks />
          </Box>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Subscription fees paid for service not yet delivered, released to revenue one month at a
            time.
          </Typography>
        </Box>
        <TextField
          select
          label="Fiscal year"
          size="small"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
          sx={{ width: 150 }}
        >
          {YEARS.map((y) => (
            <MenuItem key={y} value={y}>
              FY {y}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <ReconciliationStrip
        asOf={asOf}
        asOfNote={pickedAsOf ? null : fallback.note}
        onAsOfChange={setPickedAsOf}
        onReview={() => setView({ kind: "differences" })}
      />

      <RecognitionRunBar onHistory={() => setView({ kind: "runs" })} />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={breakdown}
        onChange={(_, v: Breakdown | null) => v && setBreakdown(v)}
        aria-label="Breakdown"
        sx={{
          mb: 2,
          "& .MuiToggleButton-root": {
            textTransform: "none",
            fontFamily: "var(--font-lato)",
            fontSize: 13.5,
            fontWeight: 700,
            px: 2.5,
            minHeight: 40,
            color: "var(--color-graphite)",
            borderColor: "var(--color-smoke)",
          },
          "& .Mui-selected": {
            color: "var(--color-white) !important",
            background: "var(--color-rodeo-black) !important",
          },
        }}
      >
        <ToggleButton value="month">By month</ToggleButton>
        <ToggleButton value="organization">By organization</ToggleButton>
      </ToggleButtonGroup>

      {breakdown === "month" ? (
        <RollForwardTable fy={fy} />
      ) : (
        <OrgDeferralsBlotter
          fy={fy}
          onOpen={(organizationId) => setView({ kind: "org", organizationId })}
        />
      )}
    </Box>
  );
}
